import { useRef, useState } from "react";
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Box,
  Button,
  FormControl,
  FormLabel,
  HStack,
  Icon,
  Select,
  Textarea,
  UnorderedList,
  ListItem,
  VStack,
} from "@chakra-ui/react";
import { FiAlertTriangle } from "react-icons/fi";
import CDS from "design-system";
import { useDeactivateUser } from "features/User/hooks";
import { User } from "features/User/models";
import { useCloseNewHireJourney } from "features/NewHireJourney/hooks";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  journeyId: string;
  newHireName: string;
  /** When true, also deactivate the new hire after the journey is closed. */
  withUserDeactivation?: boolean;
  /** Required when withUserDeactivation is true. */
  newHire?: User;
}

// Default lives at the top so users on the happy path can confirm in one
// click — most "End journey" actions happen because onboarding is genuinely
// done, not because something went wrong.
const REASON_OPTIONS = [
  "Onboarding complete",
  "Left the company",
  "Role transferred",
  "Other",
] as const;

const DEFAULT_REASON: typeof REASON_OPTIONS[number] = "Onboarding complete";

const DEACTIVATION_DEFAULT_REASON: typeof REASON_OPTIONS[number] = "Left the company";

export default function CloseJourneyModal({
  isOpen, onClose, journeyId, newHireName, withUserDeactivation = false, newHire,
}: Props) {
  const cancelRef = useRef<HTMLButtonElement>(null);
  const [reason, setReason] = useState<string>(
    withUserDeactivation ? DEACTIVATION_DEFAULT_REASON : DEFAULT_REASON,
  );
  const [otherDetails, setOtherDetails] = useState("");
  const { mutate: closeJourney, isPending: closing } = useCloseNewHireJourney(journeyId);
  const { mutate: deactivateUser, isPending: deactivating } = useDeactivateUser(
    newHire ?? ({} as User),
  );
  const isPending = closing || deactivating;

  const resolvedReason = reason === "Other" ? (otherDetails.trim() || undefined) : reason;

  const resetForm = () => {
    setReason(withUserDeactivation ? DEACTIVATION_DEFAULT_REASON : DEFAULT_REASON);
    setOtherDetails("");
  };

  const finishAndClose = () => {
    resetForm();
    onClose();
  };

  const handleConfirm = () => {
    closeJourney(
      { closeReason: resolvedReason },
      {
        onSuccess: () => {
          if (withUserDeactivation && newHire?.id) {
            deactivateUser(undefined, {
              onSuccess: finishAndClose,
            });
            return;
          }
          finishAndClose();
        },
      },
    );
  };

  const handleClose = () => {
    if (isPending) return;
    resetForm();
    onClose();
  };

  const confirmLabel = withUserDeactivation ? "End journey & deactivate user" : "End journey early";
  const confirmColorScheme = withUserDeactivation ? "red" : "brand";
  const headerText = withUserDeactivation
    ? `End journey and deactivate ${newHireName}?`
    : `End ${newHireName}'s onboarding journey early?`;

  return (
    <AlertDialog
      isOpen={isOpen}
      leastDestructiveRef={cancelRef}
      onClose={handleClose}
      isCentered
    >
      <AlertDialogOverlay>
        <AlertDialogContent>
          <AlertDialogHeader fontSize="lg" fontWeight="semibold">
            {headerText}
          </AlertDialogHeader>

          <AlertDialogBody>
            <Box mb={4}>
              <CDS.Text14 color="gray.600">
                Any remaining automations stop, pending calendar invites are cancelled, and the onboarding Slack channels are archived.
              </CDS.Text14>
              {withUserDeactivation && (
                <HStack
                  align="flex-start"
                  spacing={3}
                  mt={3}
                  p={3}
                  bg="red.50"
                  borderWidth={1}
                  borderColor="red.200"
                  borderRadius="md"
                >
                  <Icon as={FiAlertTriangle} color="red.500" boxSize={4} mt={0.5} aria-hidden />
                  <VStack align="start" spacing={1}>
                    <CDS.Text14 color="red.800" fontWeight="medium">
                      {newHireName} will also be deactivated.
                    </CDS.Text14>
                    <UnorderedList color="red.800" spacing={0.5} pl={4} fontSize="sm">
                      <ListItem>Access ends immediately.</ListItem>
                      <ListItem>Removed as buddy, manager, or stakeholder on other active journeys.</ListItem>
                      <ListItem>Email and Slack notifications stop.</ListItem>
                      <ListItem>Reactivating later requires re-assigning roles and notifications by hand.</ListItem>
                    </UnorderedList>
                  </VStack>
                </HStack>
              )}
              <CDS.Text14 color="gray.600" fontStyle="italic" mt={2}>
                This can&rsquo;t be undone.
              </CDS.Text14>
            </Box>
            <FormControl>
              <FormLabel fontSize="sm" color="gray.600">Reason</FormLabel>
              <Select
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                isDisabled={isPending}
              >
                {REASON_OPTIONS.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </Select>
              {reason === "Other" && (
                <Textarea
                  mt={2}
                  value={otherDetails}
                  onChange={(e) => setOtherDetails(e.target.value)}
                  placeholder="Add details (optional)"
                  rows={2}
                  isDisabled={isPending}
                />
              )}
            </FormControl>
          </AlertDialogBody>

          <AlertDialogFooter>
            <Box flex={1} />
            <Button ref={cancelRef} onClick={handleClose} variant="ghost" isDisabled={isPending}>
              Cancel
            </Button>
            <Button
              colorScheme={confirmColorScheme}
              onClick={handleConfirm}
              ml={3}
              isLoading={isPending}
              loadingText={closing ? "Ending journey..." : "Deactivating..."}
            >
              {confirmLabel}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialogOverlay>
    </AlertDialog>
  );
}
